import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { Toast } from "src/components";

const useAccountLinks = (token, isFinalisingLinkedIn) => {
    const history = useHistory();
    const location = useLocation();
    const controller = new AbortController();
    const isMountedRef = useRef(true);
    const config = {
        signal: controller.signal,
        headers: {
            Authorization: "Bearer " + token
        }
    };

    useEffect(() => {
        return () => {
            controller.abort();
            isMountedRef.current = false;
        };
    }, []);

    const [showAccountsLinked, setShowAccountsLinked] = useState(false);
    const [linkedAccounts, setLinkedAccounts] = useState([]);
    const [linkedAccountsFetched, setLinkedAccountsFetched] = useState(false);
    const [connectingLinkedInAccount, setConnectingLinkedInAccount] = useState(Boolean(isFinalisingLinkedIn));

    const retrieveLinkedAccounts = () => {
        setLinkedAccountsFetched(false);
        axios.get(`${API_URL}/companySocials/connectedAccounts`, config)
            .then((response) => {
                setLinkedAccounts((response.data));
                setShowAccountsLinked(true);
            })
            .catch(error => {
                console.error(error);
                Toast.error("Linked accounts could not be fetched.");
            })
            .finally(() => {
                setLinkedAccountsFetched(true);
            });
    };

    const clearUrlQuery = () => {
        history.replace({
            pathname: location.pathname,
            search: ""
        });
    };

    const saveFacebookDetails = (accessToken) => {
        setShowAccountsLinked(false);
        setLinkedAccountsFetched(false);

        const data = { accessToken };
        axios.post(`${API_URL}/companySocials/facebook/link`, data, config)
            .then((response) => {
                setLinkedAccounts(response.data);
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
            }).catch((err) => {
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
                return Toast.error(err.getApiErrorDetails().message);
            });
    };

    const saveInstagramDetails = (oauthCode) => {
        setShowAccountsLinked(false);
        setLinkedAccountsFetched(false);
        const data = {
            "code": oauthCode
        };
    
        axios.post(`${API_URL}/companySocials/instagram/link`, data, config)
            .then((response) => {
                setLinkedAccounts(response.data);
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
            }).catch((err) => {
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
                return Toast.error(err.getApiErrorDetails().message);
            });
    };

    const queryAvailableLinkedInOrganisations = code => {
        const data = {
            code
        };
        return axios.post(`${API_URL}/companySocials/linkedin/queryOrganisations`, data, config)
            .then((response) => response.data);
    };

    const saveLinkedInDetails = (code, selectedOrganisationIds, longLivedToken) => {
        setShowAccountsLinked(false);
        setLinkedAccountsFetched(false);
        const data = {
            code,
            selectedOrganisationIds,
            longLivedToken
        };

        return axios.post(`${API_URL}/companySocials/linkedin/link`, data, config)
            .then((response) => {
                setLinkedAccounts(response.data);
                return Toast.success("Successfully connected LinkedIn account.");
            }).catch((err) => {
                return Toast.error(err.getApiErrorDetails().message);
            }).finally(() => {
                dismissLinkedInLinking();
            });
    };

    const dismissLinkedInLinking = () => {
        setShowAccountsLinked(true);
        setLinkedAccountsFetched(true);
        setConnectingLinkedInAccount(false);
        clearUrlQuery(); // We got here with ?connectType=linkedIn... in the URL; this shouldn't be used again
    };

    const revokeSocialMediaAccount = (socialMediaAdminUuid) => {
        setLinkedAccountsFetched(false);
        axios.delete(`${API_URL}/companySocials/${socialMediaAdminUuid}/revoke`, config)
            .then((response) => {
                Toast.success("Selected account has been disconnected.");
            }).catch((err) => {
                Toast.error(err.getApiErrorDetails().message);
            }).finally(() => {
                retrieveLinkedAccounts();
            });
    };

    /**
     * this is called from reconnectedSocialMediaPlatform and fires the Facebook
     * login window, and on success will call @see reconnectFacebookDetails
     * and send necessary tokens to api
     * @param {string} socialMediaAdminUuid 
     */
    const handleFacebookReconnect = (socialMediaAdminUuid) => {
        if (!window.FB) {
            console.error("Facebook SDK is still loading...");
            return;
        }

        setShowAccountsLinked(false);
        setLinkedAccountsFetched(false);
        window.FB.login(
            function(response) {
                if (response.authResponse) {
                    const authResponse = response.authResponse; // short-lived access token
                    const { accessToken, expiresIn } = authResponse;
                    reconnectFacebookDetails(accessToken, expiresIn, socialMediaAdminUuid);
                } else {
                    console.error("Facebook login failed. Please try again.");
                }
            },
            { scope: "pages_manage_posts,pages_read_engagement,business_management" } // Ensure permissions are requested
        );
    };
    
    /**
     * Sends a request to API to update provided Social Media Page Admin which perform
     * necessary checks 
     * @param {string} accessToken
     * @param {number} expiresIn
     * @param {string} socialMediaPageAdminUuid
     */
    const reconnectFacebookDetails = (accessToken, expiresIn, socialMediaPageAdminUuid) => {
        setShowAccountsLinked(false);
        setLinkedAccountsFetched(false);

        const data = {
            accessToken,
            expiryDate: expiresIn
        };
        axios.post(`${API_URL}/companySocials/${socialMediaPageAdminUuid}/reconnect`, data, config)
            .then((response) => {
                setLinkedAccounts(response.data);
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
            }).catch((err) => {
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
                return Toast.error(err.getApiErrorDetails().message);
            });
    };

    /**
     * Sends a request to API to update provided Social Media Page Admin which perform
     * necessary checks
     * @param {string} oauthCode
     * @param {string} socialMediaPageAdminUuid
     */
    const handleInstagramReconnect = (oauthCode, socialMediaPageAdminUuid) => {
        setShowAccountsLinked(false);
        setLinkedAccountsFetched(false);

        const data = {
            code: oauthCode,
            expiryDate: null
        };
        axios.post(`${API_URL}/companySocials/${socialMediaPageAdminUuid}/reconnect`, data, config)
            .then((response) => {
                setLinkedAccounts(response.data);
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
            }).catch((err) => {
                setShowAccountsLinked(true);
                setLinkedAccountsFetched(true);
                return Toast.error(err.getApiErrorDetails().message);
            });
    };

    return { 
        retrieveLinkedAccounts,
        saveFacebookDetails,
        saveInstagramDetails,
        queryAvailableLinkedInOrganisations,
        saveLinkedInDetails,
        dismissLinkedInLinking,
        revokeSocialMediaAccount,
        handleFacebookReconnect,
        handleInstagramReconnect,
        showAccountsLinked,
        linkedAccounts,
        linkedAccountsFetched,
        connectingLinkedInAccount,
        setConnectingLinkedInAccount
    };
};
export default useAccountLinks;
